/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef } from 'react'

const ThemeContext = createContext()

export const THEMES = [
  { id: 'light', label: 'Light', desc: 'Warm Off-White', preview: ['#FAFAF9', '#78716C'] },
  { id: 'dark',  label: 'Dark',  desc: 'Warm Stone',    preview: ['#0C0A09', '#A1A1AA'] },
]

let isTransitioning = false
let queuedNextId = null
let queuedApplyTheme = null
let lastAnimations = []

function ensureNoBlurStyle() {
  if (document.getElementById('vt-no-blur-style')) return
  const style = document.createElement('style')
  style.id = 'vt-no-blur-style'
  style.textContent = `
    .vt-no-blur, .vt-no-blur * {
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }
  `
  document.head.appendChild(style)
}

async function runTransition(applyTheme, nextId) {
  isTransitioning = true
  ensureNoBlurStyle()
  document.documentElement.classList.add('vt-no-blur')

  const transition = document.startViewTransition(() => {
    applyTheme(nextId)
  })

  try {
    await transition.ready
    for (const anim of lastAnimations) {
      try { anim.cancel() } catch { /* already finished */ }
    }
    const DURATION = 300
    const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
    const oldAnim = document.documentElement.animate(
      [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.008)' }],
      { duration: DURATION, easing: EASING, fill: 'forwards', pseudoElement: '::view-transition-old(root)' }
    )
    const newAnim = document.documentElement.animate(
      [{ opacity: 0, transform: 'scale(0.992)' }, { opacity: 1, transform: 'scale(1)' }],
      { duration: DURATION, easing: EASING, fill: 'forwards', pseudoElement: '::view-transition-new(root)' }
    )
    lastAnimations = [oldAnim, newAnim]
  } catch { /* view transition aborted */ }

  try {
    await Promise.race([
      transition.finished,
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ])
  } catch { /* timed out */ }

  document.documentElement.classList.remove('vt-no-blur')
  isTransitioning = false

  if (queuedNextId !== null) {
    const nextQueuedId = queuedNextId
    const nextQueuedApply = queuedApplyTheme
    queuedNextId = null
    queuedApplyTheme = null
    runTransition(nextQueuedApply, nextQueuedId)
  }
}

function playTransition(applyTheme, nextId) {
  if (!document.startViewTransition) {
    applyTheme(nextId)
    return
  }
  if (isTransitioning) {
    queuedNextId = nextId
    queuedApplyTheme = applyTheme
    return
  }
  runTransition(applyTheme, nextId)
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'light'
  })

  const themeRef = useRef(theme)

  useEffect(() => {
    themeRef.current = theme
    localStorage.setItem('theme', theme)
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  const applyTheme = (id) => {
    themeRef.current = id
    setTheme(id)
  }

  const toggle = () => {
    const nextId = themeRef.current === 'light' ? 'dark' : 'light'
    themeRef.current = nextId
    setTheme(nextId)
  }

  const setThemeById = (id) => {
    if (!THEMES.find(t => t.id === id)) return
    if (id === themeRef.current) return
    playTransition(applyTheme, id)
  }

  const toggleWithRay = () => {
    const nextId = themeRef.current === 'light' ? 'dark' : 'light'
    playTransition(applyTheme, nextId)
  }

  const resetTheme = () => {
    localStorage.removeItem('theme')
    themeRef.current = 'light'
    setTheme('light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggle, setThemeById, toggleWithRay, resetTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within ThemeProvider')
  return context
}
